import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Wrench, Plus, X, CheckCircle2, AlertTriangle, Calendar,
  Clock, Settings, ClipboardList
} from "lucide-react";

const MAINT_TYPES = [
  { value: "preventive", label: "Preventiu" },
  { value: "corrective", label: "Correctiu" },
  { value: "inspection", label: "Inspecció" },
  { value: "calibration", label: "Calibratge" },
];
const FREQUENCIES = [
  { value: "weekly", label: "Setmanal" },
  { value: "biweekly", label: "Quinzenal" },
  { value: "monthly", label: "Mensual" },
  { value: "quarterly", label: "Trimestral" },
  { value: "biannual", label: "Semestral" },
  { value: "annual", label: "Anual" },
];

export default function Maintenance() {
  const { currentVenue } = useVenue();
  const [schedules, setSchedules] = useState([]);
  const [records, setRecords] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [showRecordForm, setShowRecordForm] = useState(false);
  const [schedForm, setSchedForm] = useState({ equipment_name: "", type: "preventive", frequency: "monthly", description: "", next_due: "" });
  const [recForm, setRecForm] = useState({ equipment_name: "", type: "preventive", description: "", technician: "", notes: "" });

  useEffect(() => { if (currentVenue) loadAll(); }, [currentVenue]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, r, eq] = await Promise.all([
        hosys.entities.getAll("maintenance_schedules", currentVenue.id, "-next_due"),
        hosys.entities.getAll("maintenance_records", currentVenue.id, "-date"),
        hosys.entities.getAll("equipment_configs", currentVenue.id),
      ]);
      setSchedules(s);
      setRecords(r);
      setEquipment(eq);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleAddSchedule = async () => {
    if (!schedForm.equipment_name || !schedForm.next_due) return;
    await hosys.entities.create("maintenance_schedules", {
      ...schedForm, venue_id: currentVenue.id, active: true,
    });
    setShowScheduleForm(false);
    setSchedForm({ equipment_name: "", type: "preventive", frequency: "monthly", description: "", next_due: "" });
    loadAll();
  };

  const handleAddRecord = async () => {
    if (!recForm.equipment_name) return;
    await hosys.entities.create("maintenance_records", {
      ...recForm, venue_id: currentVenue.id,
      date: new Date().toISOString().slice(0, 10),
      status: "completed",
    });
    setShowRecordForm(false);
    setRecForm({ equipment_name: "", type: "preventive", description: "", technician: "", notes: "" });
    loadAll();
  };

  if (loading) return <QuickLoader />;

  const today = new Date().toISOString().slice(0, 10);
  const overdue = schedules.filter(s => s.next_due && s.next_due < today && s.active);

  return (
    <div className="space-y-5" data-testid="maintenance-page">
      <PageHeader title="Manteniment" subtitle="Programació i registre de manteniment d'equips" />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-4 border-0 shadow-sm bg-gray-50 text-center">
          <p className="text-2xl font-bold text-gray-800">{equipment.length}</p>
          <p className="text-xs text-gray-500">Equips</p>
        </Card>
        <Card className="p-4 border-0 shadow-sm bg-blue-50 text-center">
          <p className="text-2xl font-bold text-blue-700">{schedules.filter(s => s.active).length}</p>
          <p className="text-xs text-gray-500">Programacions</p>
        </Card>
        <Card className={`p-4 border-0 shadow-sm text-center ${overdue.length > 0 ? "bg-red-50" : "bg-emerald-50"}`}>
          <p className={`text-2xl font-bold ${overdue.length > 0 ? "text-red-700" : "text-emerald-700"}`}>{overdue.length}</p>
          <p className="text-xs text-gray-500">Endarrerits</p>
        </Card>
        <Card className="p-4 border-0 shadow-sm bg-purple-50 text-center">
          <p className="text-2xl font-bold text-purple-700">{records.length}</p>
          <p className="text-xs text-gray-500">Registres</p>
        </Card>
      </div>

      {overdue.length > 0 && (
        <div className="rounded-2xl bg-red-50 border border-red-200 p-4 flex items-start gap-3" data-testid="maintenance-overdue-alert">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-red-800">{overdue.length} manteniment(s) endarrerit(s)</p>
            <p className="text-xs text-red-600">{overdue.map(o => o.equipment_name).join(", ")}</p>
          </div>
        </div>
      )}

      <Tabs defaultValue="schedules">
        <TabsList className="grid grid-cols-2 h-auto gap-1 p-1 bg-gray-100 rounded-xl">
          <TabsTrigger value="schedules" className="rounded-lg text-xs py-2"><Settings className="w-3.5 h-3.5 mr-1 inline" />Programació</TabsTrigger>
          <TabsTrigger value="records" className="rounded-lg text-xs py-2"><ClipboardList className="w-3.5 h-3.5 mr-1 inline" />Registres</TabsTrigger>
        </TabsList>

        <TabsContent value="schedules" className="mt-4 space-y-3">
          <Button onClick={() => setShowScheduleForm(!showScheduleForm)} className="bg-[#7B2D3F] hover:bg-[#7B2D3F]/90" data-testid="new-schedule-btn">
            <Plus className="w-4 h-4 mr-1" /> Nova programació
          </Button>
          {showScheduleForm && (
            <Card className="p-4 border-0 shadow-sm" data-testid="schedule-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Equip</label>
                  <select className="w-full border rounded-lg px-3 py-2 text-sm" value={schedForm.equipment_name} onChange={e => setSchedForm({...schedForm, equipment_name: e.target.value})}>
                    <option value="">Selecciona...</option>
                    {equipment.map((e) => <option key={e.id || e.name} value={e.name}>{e.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Tipus</label>
                  <select className="w-full border rounded-lg px-3 py-2 text-sm" value={schedForm.type} onChange={e => setSchedForm({...schedForm, type: e.target.value})}>
                    {MAINT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Freqüència</label>
                  <select className="w-full border rounded-lg px-3 py-2 text-sm" value={schedForm.frequency} onChange={e => setSchedForm({...schedForm, frequency: e.target.value})}>
                    {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Pròxim manteniment</label>
                  <Input type="date" value={schedForm.next_due} onChange={e => setSchedForm({...schedForm, next_due: e.target.value})} />
                </div>
              </div>
              <Input placeholder="Descripció..." className="mb-3" value={schedForm.description} onChange={e => setSchedForm({...schedForm, description: e.target.value})} />
              <Button onClick={handleAddSchedule} className="bg-[#7B2D3F] hover:bg-[#7B2D3F]/90">Guardar</Button>
            </Card>
          )}
          {schedules.filter(s => s.active).length === 0 ? (
            <Card className="p-8 border-0 shadow-sm text-center">
              <Settings className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Cap programació de manteniment.</p>
            </Card>
          ) : schedules.filter(s => s.active).map((s, i) => {
            const isOverdue = s.next_due && s.next_due < today;
            const freq = FREQUENCIES.find(f => f.value === s.frequency)?.label || s.frequency;
            const tp = MAINT_TYPES.find(t => t.value === s.type)?.label || s.type;
            return (
              <Card key={s.id || s.maintenance_schedule_id || i} className={`p-4 border-0 shadow-sm ${isOverdue ? "border-l-4 border-l-red-500" : ""}`} data-testid={`schedule-${i}`}>
                <div className="flex items-center gap-3">
                  <Wrench className={`w-5 h-5 ${isOverdue ? "text-red-500" : "text-gray-400"}`} />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">{s.equipment_name}</p>
                    <div className="flex gap-1 mt-1">
                      <Badge className="bg-gray-100 text-gray-600 border-0 text-[10px]">{tp}</Badge>
                      <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px]">{freq}</Badge>
                    </div>
                    {s.description && <p className="text-xs text-gray-500 mt-1">{s.description}</p>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`text-xs font-semibold ${isOverdue ? "text-red-600" : "text-gray-600"}`}>
                      {isOverdue ? "ENDARRERIT" : "Pròxim"}
                    </p>
                    <p className="text-xs text-gray-500">{s.next_due}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </TabsContent>

        <TabsContent value="records" className="mt-4 space-y-3">
          <Button onClick={() => setShowRecordForm(!showRecordForm)} className="bg-[#7B2D3F] hover:bg-[#7B2D3F]/90" data-testid="new-record-btn">
            <Plus className="w-4 h-4 mr-1" /> Nou registre
          </Button>
          {showRecordForm && (
            <Card className="p-4 border-0 shadow-sm" data-testid="record-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Equip</label>
                  <select className="w-full border rounded-lg px-3 py-2 text-sm" value={recForm.equipment_name} onChange={e => setRecForm({...recForm, equipment_name: e.target.value})}>
                    <option value="">Selecciona...</option>
                    {equipment.map((e) => <option key={e.id || e.name} value={e.name}>{e.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Tipus</label>
                  <select className="w-full border rounded-lg px-3 py-2 text-sm" value={recForm.type} onChange={e => setRecForm({...recForm, type: e.target.value})}>
                    {MAINT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              <Input placeholder="Tècnic..." className="mb-3" value={recForm.technician} onChange={e => setRecForm({...recForm, technician: e.target.value})} />
              <Textarea placeholder="Descripció del treball realitzat..." className="mb-3" value={recForm.description} onChange={e => setRecForm({...recForm, description: e.target.value})} />
              <Button onClick={handleAddRecord} className="bg-[#7B2D3F] hover:bg-[#7B2D3F]/90">Registrar</Button>
            </Card>
          )}
          {records.length === 0 ? (
            <Card className="p-8 border-0 shadow-sm text-center">
              <ClipboardList className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Cap registre de manteniment.</p>
            </Card>
          ) : records.map((r, i) => {
            const tp = MAINT_TYPES.find(t => t.value === r.type)?.label || r.type;
            return (
              <Card key={r.id || r.maintenance_record_id || i} className="p-4 border-0 shadow-sm" data-testid={`record-${i}`}>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">{r.equipment_name}</p>
                    <div className="flex gap-1 mt-1">
                      <Badge className="bg-gray-100 text-gray-600 border-0 text-[10px]">{tp}</Badge>
                      {r.technician && <Badge className="bg-purple-100 text-purple-700 border-0 text-[10px]">{r.technician}</Badge>}
                    </div>
                    {r.description && <p className="text-xs text-gray-500 mt-1">{r.description}</p>}
                  </div>
                  <p className="text-xs text-gray-400">{r.date}</p>
                </div>
              </Card>
            );
          })}
        </TabsContent>
      </Tabs>
    </div>
  );
}
